import InfoCard from "@components/molecules/InfoCard";
import CircularButtonWithIcon from "@components/molecules/CircularButtonWithIcon";
import { FaPersonRunning, FaCalendarDays, FaHeartPulse, FaCalendarPlus, FaGear } from "react-icons/fa6";
import { HttpError, useGo, useList, useTranslation, Link, useGetIdentity } from "@refinedev/core";
import { blogger_v3, calendar_v3 } from "googleapis"
import { useAtomValue } from "jotai";
import { blogIdAtom } from "@pages/content/state";
import { useMemo } from "react";
import dayjs from "dayjs";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { ContentCard } from "@components/organisms/ContentCard";
import { EventCode, searchMetadataString } from "core/calendar/definitions";
import { AppointmentEvent } from "@components/atoms/AppointmentEvent";
import { User_User_Public } from "@graphql/schema.types";
import { useList as useListRefine } from "@refinedev/core";
import { FaSearch } from "react-icons/fa";
import { BillingInfoForm } from "@components/organisms/BillingInfoForm";
import { useBillingInfo } from "hooks/useBillingInfo";

export const HomeUser = () => {
    const { translate } = useTranslation()
    const go = useGo()
    const { data: identity } = useGetIdentity<{ name?: string; display_name?: string }>();
    const userName = identity?.display_name?.split(' ')?.[0] || identity?.name?.split(" ")?.[0] || "";
    const { hasBillingInfo, isLoading: isLoadingBillingInfo, refetch: refetchBillingInfo } = useBillingInfo();

    /*
    * Appointments
    */
    const calendarId = useAppCalendar()

    const date = useMemo(() => dayjs(), [])
    const {
        data: events,
        isLoading: isLoadingAppoiments,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        dataProviderName: 'gapi',
        resource: `calendar/calendars/:calendar/events`,
        meta: {
            params: {
                calendar: calendarId?.appointments
            }
        },
        queryOptions: {
            enabled: !!calendarId,
        },
        sorters: [
            {
                field: 'startTime',
                order: 'asc'
            }
        ],
        pagination: {
            pageSize: 1,
        },
        filters: [
            {
                field: 'timeMin',
                operator: 'eq',
                value: date.toISOString()
            },
            {
                field: 'showDeleted',
                operator: 'eq',
                value: false
            },
            {
                field: 'singleEvents',
                operator: 'eq',
                value: true
            },
            {
                field: 'q',
                operator: 'eq',
                value: searchMetadataString({ code: EventCode.APPOINTMENT })
            }
        ]
    })
    const followingEvent = events?.data[0]
    const followingDate = followingEvent ? dayjs(followingEvent.start?.dateTime) : null

    // Get user email from attendees (not a service account)
    const userEmail = followingEvent?.attendees?.find(a => a?.email && !a.email.includes('gserviceaccount'))?.email
    // Fetch user public info for contact card
    const { data: userContactData } = useListRefine<User_User_Public, HttpError, User_User_Public>({
        resource: 'user_user_public',
        filters: [
            {
                field: 'email',
                operator: 'eq',
                value: userEmail,
            },
        ],
        queryOptions: {
            enabled: !!userEmail,
        },
        meta: {
            fields: [
                'email',
                'avatar_url',
                'display_name',
            ],
        },
    })
    const userContact = userContactData?.data?.[0]

    /*
    * Content
    */
    const blogId = useAtomValue(blogIdAtom)

    const {
        data,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: `blogger/blogs/:blogId/posts`, //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            }
        },
        queryOptions: {
            enabled: !!blogId,
        },
        sorters: [
            {
                field: 'PUBLISHED',
                order: 'desc'
            }
        ],
        pagination: {
            pageSize: 5,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'fetchImages',
                operator: 'eq',
                value: true
            }
        ]
    })
    const posts = data?.data

    // Mostrar loading mientras verifica si ya tiene registro de facturación
    if (isLoadingBillingInfo) {
        return (
            <div className="flex flex-col min-h-screen w-full">
                <header className=" md:h-auto md:ml-48 relative">
                    <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                    <div className="w-full bg-secondary pb-22 md:pb-8 md:pl-12 pt-6 px-4 rounded-bl-[4rem] md:rounded-bl-4xl relative overflow-hidden">
                        <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-40 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom" />
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-2xl font-bold text-base-100">
                                {userName
                                    ? translate("page.home.greeting", { name: userName })
                                    : translate("page.home.greeting.noName")}
                            </h1>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-4 py-6 flex items-center justify-center">
                    <div className="loading loading-spinner loading-lg"></div>
                </main>
            </div>
        );
    }

    // FOrmulario si no hay registro en la tabla de facturación
    if (!hasBillingInfo) {
        return (
            <div className="flex flex-col min-h-screen w-full">
                <header className=" md:h-auto md:ml-48 relative">
                    <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                    <div className="w-full bg-secondary pb-22 md:pb-8 md:pl-12 pt-6 px-4 rounded-bl-[4rem] md:rounded-bl-4xl relative overflow-hidden">
                        <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-40 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom" />
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-2xl font-bold text-base-100">
                                {userName
                                    ? translate("page.home.greeting", { name: userName })
                                    : translate("page.home.greeting.noName")}
                            </h1>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-4 py-6">
                    <BillingInfoForm onSuccess={refetchBillingInfo} />
                </main>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen w-full">
            {/* Top Buttons Section */}
            <header className=" md:h-auto md:ml-48 relative">
                <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                <div className="w-full bg-secondary pb-22 md:pb-8 md:pl-12 pt-6 px-4 rounded-bl-[4rem] md:rounded-bl-4xl relative overflow-hidden">
                    <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-40 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom" />
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-base-100">
                            {userName
                                ? translate("page.home.greeting", { name: userName })
                                : translate("page.home.greeting.noName")}
                        </h1>
                        <FaSearch className="w-6 h-6 text-base-100" />
                    </div>
                    <nav className="flex flex-wrap gap-4 justify-center sm:justify-start" aria-label="Main quick actions">
                        <Link to="/helps" className="no-underline">
                            <CircularButtonWithIcon
                                data-tip={translate("page.home.topButtons.help.tip")}
                                text={translate("page.home.topButtons.help.title")}
                                icon={FaHeartPulse}
                                className="sm:w-auto" />
                        </Link>
                        <CircularButtonWithIcon
                            data-tip={translate("page.home.topButtons.assistance.tip")}
                            text={translate("page.home.topButtons.assistance.title")}
                            icon={FaPersonRunning}
                            className="sm:w-auto" />
                        <Link
                            className="no-underline"
                            go={{
                                to: {
                                    resource: "appointment",
                                    action: "create"
                                },
                            }}>
                            <CircularButtonWithIcon data-tip={translate("page.home.topButtons.schedule.tip")}
                                text={translate("page.home.topButtons.schedule.title")}
                                icon={FaCalendarPlus}
                                className="sm:w-auto" />
                        </Link>
                        <Link
                            className="no-underline"
                            go={{
                                to: {
                                    resource: "config",
                                    action: "list"
                                },
                            }}>
                            <CircularButtonWithIcon data-tip={translate("page.home.topButtons.config.tip")}
                                text={translate("page.home.topButtons.config.title")}
                                icon={FaGear}
                                className="sm:w-auto" />
                        </Link>
                    </nav>
                </div>
            </header>
            <main className="flex-1 px-4">
                {/* Next Appointment Section */}
                <section className="my-6" aria-labelledby="next-appointment-heading">
                    <h2 id="next-appointment-heading" className="text-lg sm:text-xl font-semibold my-2">{translate("page.home.new-appointment")}</h2>
                    <div className="dropdown w-full">
                        <div tabIndex={0} role="button" className="w-full">
                            <InfoCard
                                loading={isLoadingAppoiments}
                                title={translate('page.home.next-appointment')}
                                description={!isLoadingAppoiments ? (followingDate ? (
                                    <>
                                        <span className="capitalize">{`${followingDate.format('dddd, HH:mm')}`}</span>
                                        <span className="badge badge-primary ml-2">{`${date.to(followingDate)}`}</span>
                                    </>
                                ) : translate('page.home.no-following-appointment')) : '-'}
                                icon={FaCalendarDays}
                            />
                        </div>
                        {followingEvent && (
                            <AppointmentEvent
                                tabIndex={0}
                                className="dropdown-content z-10 mt-1 not-prose w-full sm:w-auto"
                                event={followingEvent}
                                contact={userContact}
                                name="user"
                            />
                        )}
                    </div>
                </section>
                {/* News/Knowledge Section */}
                <section className="mb-6" aria-labelledby="news-knowledge-heading">
                    <h2 id="news-knowledge-heading" className="text-lg sm:text-xl font-semibold">{`${translate("page.home.news")} / ${translate("page.home.knowledge")}`}</h2>
                    <div className="carousel carousel-center w-full">
                        {(!posts || posts.length === 0) ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="carousel-item w-[90%] md:w-[45%] lg:w-[30%] xl:w-[25%] p-2">
                                    <div className="h-90 w-full bg-base-200 rounded-xl skeleton flex flex-col justify-end p-4">
                                    </div>
                                </div>
                            ))
                        ) : (
                            posts.map(post => (
                                <div key={post.id!} className="carousel-item w-[90%] md:w-[45%] lg:w-[30%] xl:w-[25%] p-2">
                                    <ContentCard post={post} />
                                </div>
                            ))
                        )}
                    </div>
                </section>
            </main>
        </div>
    )
}
